import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Snackbar,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import {
  fetchCustomers,
  deleteCustomer,
  updateCustomer,
  GetCountry,
  GetCurrency,
  GetPayTerms,
} from "../../services/customerService";
import { Customer } from "../../Interface/Customer";
import { AgGridTable } from "../../Component/AgGridTable";
import { EnhancedPageTitle } from "../../Component/PageTitle";
import GroupIcon from "@mui/icons-material/Group";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import { SearchDrawer, FilterField } from "../../Component/SearchDrawer";

const Customers = () => {
  const [customerData, setCustomerData] = useState<Customer[]>([]);
  const [allCustomers, setAllCustomers] = useState<Customer[]>([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [countries, setCountries] = useState<any[]>([]);
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [payTerms, setPayTerms] = useState<any[]>([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<Customer | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmUpdateOpen, setConfirmUpdateOpen] = useState(false);
const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");

  const navigate = useNavigate();

  useEffect(() => {
    loadCustomers();
    loadMasterData();
  }, []);

  const loadCustomers = async () => {
    try {
      const customers = await fetchCustomers();
      setCustomerData(customers);
      setAllCustomers(customers);
    } catch (error) {
      console.error("Error fetching customers", error);
    }
  };

  const handleReset = () => {
    setCustomerData(allCustomers);
    setDrawerOpen(false);
  };

  const loadMasterData = async () => {
    try {
      const [countryRes, currencyRes, payTermsRes] = await Promise.all([
        GetCountry(),
        GetCurrency(),
        GetPayTerms(),
      ]);
      setCountries(countryRes);
      setCurrencies(currencyRes);
      setPayTerms(payTermsRes);
    } catch (err) {
      console.error("Master data fetch error:", err);
    }
  };

  const handleEdit = (customer: Customer) => {
    const matchedCountry = countries.find(
      (c) => c.tableName?.toLowerCase() === customer.country?.toLowerCase()
    );

    const matchedCurrency = currencies.find(
      (c) => c.tableName?.toLowerCase() === customer.currency?.toLowerCase()
    );

    const matchedPayTerms = payTerms.find(
      (p) => p.tableName?.toLowerCase() === customer.payTerms?.toLowerCase()
    );

    setSelectedCustomer({
      ...customer,
      countryId: matchedCountry?.tableID ?? "",
      currencyId: matchedCurrency?.tableID ?? "",
      payTermsId: matchedPayTerms?.tableID ?? "",
    });

    setEditDialogOpen(true);
  };

const handleDelete = async () => {
  if (!customerToDelete) return;

  try {
    const response = await deleteCustomer(customerToDelete.customerId);

    // 🟠 Check if backend returned a soft-error message in data
    if (
      typeof response?.data === "string" &&
      response.data.startsWith("Cannot delete")
    ) {
      // 👉 Manually throw so it goes to catch block
      throw new Error(response.data);
    }

    // ✅ If not, treat it as success
    setSnackbarMessage("Customer deleted successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    loadCustomers();
  } catch (error: any) {
    console.error("Delete failed:", error);

    const backendMessage =
      error?.response?.data?.data || // backend response
      error?.message ||              // manually thrown
      "Deletion failed.";            // fallback

    setSnackbarMessage(backendMessage);
    setSnackbarSeverity("error");
    setSnackbarOpen(true);
  } finally {
    setConfirmDialogOpen(false);
    setCustomerToDelete(null);
  }
};





  const handleUpdate = async () => {
    if (!selectedCustomer || !selectedCustomer.customerId) return;

    const payload = {
      customerId: selectedCustomer.customerId,
      customerName: selectedCustomer.customerName,
      countryId: Number(selectedCustomer.countryId),
      currencyId: Number(selectedCustomer.currencyId),
      payTermsId: Number(selectedCustomer.payTermsId),
      primaryContact: selectedCustomer.primaryContact,
      isActive: true,
      isDeleted: false,
      createdBy: 0,
      createdOn: new Date().toISOString(),
      modifiedBy: 0,
      modifiedOn: new Date().toISOString(),
    };

    try {
      setLoading(true);
      await updateCustomer(payload);
      setEditDialogOpen(false);
      setSelectedCustomer(null);
      await loadCustomers();
      setSnackbarMessage("Customer updated successfully!");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Update failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterFields: FilterField[] = [
   
    {
      key: "country",
      label: "Country",
      type: "dropdown",
      options: countries.map((c) => ({ label: c.tableName, value: c.tableName })),
    },
    {
      key: "currency",
      label: "Currency",
      type: "dropdown",
      options: currencies.map((c) => ({ label: c.tableName, value: c.tableName })),
    },
    {
      key: "payTerms",
      label: "Payment Terms",
      type: "dropdown",
      options: payTerms.map((p) => ({ label: p.tableName, value: p.tableName })),
    },
  ];

  const handleSearch = (filters: Record<string, any>) => {
    const filtered = allCustomers.filter((cust) => {
      return Object.entries(filters).every(([key, value]) => {
        const [field, operator] = key.split(":");
        const customerValue = cust[field as keyof Customer];

        if (operator === "lk") {
          return (
            typeof customerValue === "string" &&
            customerValue.toLowerCase().includes(value.toLowerCase())
          );
        }

        if (operator === "eq") {
          return customerValue?.toString() === value.toString();
        }

        return true;
      });
    });

    setCustomerData(filtered);
    setDrawerOpen(false);
  };

  const columns = [
    { field: "customerName", headerName: "Customer Name",filter:true },
    { field: "country", headerName: "Country",filter:true },
    { field: "currency", headerName: "Currency",filter:true },
    { field: "primaryContact", headerName: "Primary Contact",filter:true },
    { field: "payTerms", headerName: "Payment Terms",filter:true },
    {
      headerName: "Actions",
      field: "actions",
      cellRenderer: (params: any) => {
        const customer = params.data;
        return (
          <Stack direction="row" spacing={1}>
            <IconButton size="small" color="primary" onClick={() => handleEdit(customer)}>
              <EditIcon />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => {
                setCustomerToDelete(customer);
                setConfirmDialogOpen(true);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        );
      },
    },
  ];

  return (
    <Box sx={{ padding: 3, backgroundColor: "rgb(238, 242, 246)" }}>
      <EnhancedPageTitle
        title="Customer Master"
        icon={<GroupIcon />}
        button1Text="Add New"
        button1Action={() => navigate("/AddCustomer")}
        button2Text="Search"
        button2Action={() => setDrawerOpen(true)}
      />

      <SearchDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSearch={handleSearch}
        onReset={handleReset}
        filterFields={filterFields}
      />

      <AgGridTable
        domLayout="autoHeight"
        customStyles="legacy"
        rowSelection="single"
        gridOptions={{}}
        columns={columns}
        data={customerData}
        handleSelectedRows={() => { }}
        noDataComponentText="No Customer data available"
        pagination={false}
        paginationPerPage={10}
      />

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Customer</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Customer Name"
              value={selectedCustomer?.customerName ?? ""}
              onChange={(e) =>
                setSelectedCustomer((prev) =>
                  prev ? { ...prev, customerName: e.target.value } : prev
                )
              }
              fullWidth
            />

            <TextField
              label="Primary Contact"
              value={selectedCustomer?.primaryContact ?? ""}
              onChange={(e) =>
                setSelectedCustomer((prev) =>
                  prev ? { ...prev, primaryContact: e.target.value } : prev
                )
              }
              fullWidth
            />

            <TextField
              select
              label="Country"
              value={selectedCustomer?.countryId ?? ""}
              onChange={(e) =>
                setSelectedCustomer((prev) =>
                  prev ? { ...prev, countryId: Number(e.target.value) } : prev
                )
              }
              fullWidth
            >
              {countries.map((country) => (
                <MenuItem key={country.tableID} value={country.tableID}>
                  {country.tableName}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Currency"
              value={selectedCustomer?.currencyId ?? ""}
              onChange={(e) =>
                setSelectedCustomer((prev) =>
                  prev ? { ...prev, currencyId: Number(e.target.value) } : prev
                )
              }
              fullWidth
            >
              {currencies.map((currency) => (
                <MenuItem key={currency.tableID} value={currency.tableID}>
                  {currency.tableName}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Payment Terms"
              value={selectedCustomer?.payTermsId ?? ""}
              onChange={(e) =>
                setSelectedCustomer((prev) =>
                  prev ? { ...prev, payTermsId: Number(e.target.value) } : prev
                )
              }
              fullWidth
            >
              {payTerms.map((pt) => (
                <MenuItem key={pt.tableID} value={pt.tableID}>
                  {pt.tableName}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => setConfirmUpdateOpen(true)}
            variant="contained"
            color="primary"
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmUpdateOpen} onClose={() => setConfirmUpdateOpen(false)}>
        <DialogTitle>Confirm Update</DialogTitle>
        <DialogContent>
          Are you sure you want to update this customer?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmUpdateOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setConfirmUpdateOpen(false); // CLOSE the dialog immediately
              handleUpdate();              // Then proceed with the update
            }}
            variant="contained"
            color="primary"
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>


      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this customer?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

    <Snackbar
  open={snackbarOpen}
  autoHideDuration={3000}
  onClose={() => setSnackbarOpen(false)}
  anchorOrigin={{ vertical: "top", horizontal: "right" }}
>
  <MuiAlert
    onClose={() => setSnackbarOpen(false)}
    severity={snackbarSeverity}
    sx={{ width: "100%" }}
  >
    {snackbarMessage}
  </MuiAlert>
</Snackbar>

  

    </Box>
  );
};

export default Customers;